import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BehaviorSubject, filter, take } from 'rxjs';
import moment from 'moment';
import { CountryCode } from 'libphonenumber-js';
import { ReservationService } from '../../reservations/services/reservation.service';
import { claimStatusIdToStatus, getClaimNegligenceDescription, getClaimStatusColor, getClaimStatusHistoryDate } from '../ClaimHelper';
import { ClaimInquireModalComponent } from '../components/claim-inquire-modal/claim-inquire-modal.component';
import { ClaimsService } from '../services/claims.service';
import { ClaimDetailAttachmentDialogComponent } from './components/claim-detail-attachment-dialog/claim-detail-attachment-dialog.component';
import { ClaimStatusDetailRowComponent, ClaimStatusDetailRowState } from './components/claim-status-detail-row/claim-status-detail-row.component';
import { UpdateClaimStatusDialogComponent } from './components/update-claim-status-dialog/update-claim-status-dialog.component';
import { DeleteUndeleteClaimDialogComponent } from './components/delete-undelete-claim-dialog/delete-undelete-claim-dialog.component';
import { ClaimPaymentReceivedDialogComponent } from './components/claim-payment-received-dialog/claim-payment-received-dialog.component';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { ReferenceService } from '../../../shared/reference/reference.service';
import { getCountryNameFromCountryId } from '../../../utilities/CountryUtility';
import { formatCurrencyString } from '../../../utilities/CurrencyUtility';
import { dateFormat } from '../../../utilities/DateUtility';
import { ImageUtility } from '../../../utilities/ImageUtility';
import { Reservation, ReservationSelectedPolicy } from '../../reservations/models/Reservation';
import { Claim, ClaimDocument, ClaimRow, CLAIM_STATUS } from '../models/Claim';
import { ClaimDetailDateDisplayComponent } from './components/claim-detail-date-display/claim-detail-date-display.component';
import { ClaimDetailDocumentComponent } from './components/claim-detail-document/claim-detail-document.component';
import { PillComponent } from '../../../shared/components/pill/pill.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ColorProp, getColor } from '../../../layout/Colors';
import { isStringEmptyOrNull, padString } from '../../../utilities/StringUtility';
import { formatAddress } from '../../../utilities/AddressUtility';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { formatPhoneNumber } from '../../../utilities/PhoneUtility';

@Component({
  selector: 'app-claim-detail',
  templateUrl: './claim-detail.component.html',
  styleUrls: ['./claim-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatProgressSpinner, 
    MatIcon, 
    MatCardModule,
    MatButtonModule,
    RouterModule,
    MatTableModule,
    ClaimDetailDateDisplayComponent,
    ClaimDetailDocumentComponent, 
    PillComponent,
    ClaimStatusDetailRowComponent,
    BackButtonComponent
  ]
})
export class ClaimDetailComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  loadingClaimDetails: boolean = false;
  claimId!: string;
  claim?: Claim;
  reservation!: Reservation;
  insurancePolicy?: ReservationSelectedPolicy;
  currentCountryCode!: CountryCode;

  attachments: ClaimDocument[] = [];
  attachmentsExpanded = false;
  displayFullAdditionalComments = false;
  
  claimReasons: string[] = [];
  claimStatusLastUpdateDate = 'Never';
  claimSubmittedDate? = '';
  claimDraftDate? = '';
  claimCompletedDate? = '';
  claimRow!: ClaimRow;
  primaryClaimContact: any;
 
  claimStatusHistoryDataSource = new MatTableDataSource<any>();
  claimStatusHistoryColumns = ['timestamp', 'status'];
  claimStatusIdToStatus = claimStatusIdToStatus;
  ClaimStatusDetailRowState = ClaimStatusDetailRowState;
  getCountryNameFromCountryId = getCountryNameFromCountryId;
  getClaimNegligenceDescription = getClaimNegligenceDescription;
  dateFormat = dateFormat;
  formatCurrencyString = formatCurrencyString;
  formatPhoneNumber = formatPhoneNumber;

  CLAIM_STATUS_DRAFT = CLAIM_STATUS.DRAFT;

  getColor = getColor;
  colorProp = ColorProp;
  padString = padString;
  isStringEmptyOrNull = isStringEmptyOrNull;
  formatAddress = formatAddress;

  constructor(
    private route: ActivatedRoute, 
    private claimService: ClaimsService,
    private reservationService: ReservationService,
    private referenceService: ReferenceService,
    private organizationService: OrganizationService,
    private router: Router,
    private imageUtility: ImageUtility,
    public userContextProvider: UserContextProvider,
    public dialog: MatDialog,
    private logService: Logger
  ) {
    this.claimRow = this.router.getCurrentNavigation()?.extras.state?.['data'];
  }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.route.params.subscribe(params => {
        this.currentCountryCode = this.userContextProvider.countryCode as CountryCode;
        this.claimId = params['id'];
        this.load();
      });
    });
  }

  async load() {
    await this.loadClaim();
    this.attachmentsExpanded = this.attachments.length > 0;
  }

  getYesNoValue = (value: boolean): string => {
    return value ? 'Yes' : 'No';
  }

  async loadClaim() {
    try {
      this.loading.next(true);
      this.loadingClaimDetails = true;

      const claim = await this.claimService.getClaimById(this.claimId);
      if (claim) {
        this.claim = claim;
        this.reservation = await this.reservationService.getReservationById(claim.reservation.id);
        this.insurancePolicy = this.reservation?.selected_policies?.[0];

        let damageDate = '';
        try {
          damageDate = dateFormat(claim.damage_date, this.userContextProvider.region);
        } catch (e) {
          this.logService.handleError(e, `Could not convert Claim Damage Date: ${claim.damage_date} to a date`, `Could not convert Claim Damage Date: ${claim.damage_date} to a date`);
        } finally {
          this.loading.next(false);
        }

        this.claimStatusHistoryDataSource.data = claim.status_history.map(history => {
          const dateObject = new Date(history.status_date);
          const localizedTimeString = dateObject.toLocaleTimeString();
          return {
            timestampDate: history.status_date,
            timestampTime: localizedTimeString,
            status: claimStatusIdToStatus(history.status_id)
          }
        });
  
        claim.status_history = claim.status_history.sort((a, b) => new Date(b.status_date).getTime() - new Date(a.status_date).getTime());
        claim.damage_date = damageDate;
        claim.home_liveable = this.getYesNoValue(claim.home_liveable);
        claim.would_rent_again = this.getYesNoValue(claim.would_rent_again);
        this.claimStatusLastUpdateDate = dateFormat(claim.status_history[0].status_date, this.userContextProvider.region, true);
        this.claimSubmittedDate = getClaimStatusHistoryDate(CLAIM_STATUS.IN_PROGRESS, claim);
        this.claimDraftDate = getClaimStatusHistoryDate(CLAIM_STATUS.DRAFT, claim);
        this.claimCompletedDate = getClaimStatusHistoryDate(CLAIM_STATUS.COMPLETED, claim);
        this.attachments = claim.claim_documents;

        let organizationDetail = await this.organizationService.getOrganizationDetail(claim.organization_id);
        if ((await organizationDetail).members.find(member => member.account_id === claim.account_id)) {
          this.primaryClaimContact = (await organizationDetail).members.find(member => member.account_id === claim.account_id);
        } else {
          this.primaryClaimContact = undefined;
        }
        if (claim.claim_cause_damage_guest_property) {
          this.claimReasons.push("Damage to guest's personal property");
        }
        if (claim.claim_cause_damage_rental_property) {
          this.claimReasons.push("Damage to rental property");
        }
        if (claim.claim_cause_injury) {
          this.claimReasons.push("Injury");
        }
        if (claim.claim_cause_theft_fire) {
          this.claimReasons.push("Theft or Fire");
        }
        if (claim.claim_cause_other) {
          this.claimReasons.push("Other loss");
        }
  
        this.loadingClaimDetails = false;
      }
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Claim details', 'An error occurred while retrieving Claim details');
    } finally {
      this.loading.next(false);
      this.loadingClaimDetails = false;
    }
  }
  
  downloadDocument = (fileId: number): void => {
    this.referenceService.downloadFile(fileId);
  }

  getClaimSubmittedState(): ClaimStatusDetailRowState {
    switch(this.claim?.status_id) {
      case CLAIM_STATUS.DRAFT:
        return ClaimStatusDetailRowState.PENDING;
      case CLAIM_STATUS.IN_PROGRESS:
      case CLAIM_STATUS.COMPLETED:
        return ClaimStatusDetailRowState.FINISHED;
      default:
        return ClaimStatusDetailRowState.PENDING;
    }
  }

  getClaimProcessingState(): ClaimStatusDetailRowState {
    switch(this.claim?.status_id) {
      case CLAIM_STATUS.DRAFT:
        return ClaimStatusDetailRowState.PENDING;
      case CLAIM_STATUS.IN_PROGRESS:
        return ClaimStatusDetailRowState.INPROGRESS;
      case CLAIM_STATUS.COMPLETED:
        return ClaimStatusDetailRowState.FINISHED;
      default:
        return ClaimStatusDetailRowState.PENDING;
    }
  }

  getClaimApprovedState(): ClaimStatusDetailRowState {
    switch(this.claim?.status_id) {
      case CLAIM_STATUS.DRAFT:
      case CLAIM_STATUS.IN_PROGRESS:
        return ClaimStatusDetailRowState.PENDING;
      case CLAIM_STATUS.COMPLETED:
        return ClaimStatusDetailRowState.FINISHED;
      default:
        return ClaimStatusDetailRowState.PENDING;
    }
  }

  getClaimStatusText(): string {
    let claimStatusText;

    if (this.claim?.is_active === 0) {
      claimStatusText = 'Deleted';
    } else {
      const claimStatus = claimStatusIdToStatus(this.claim?.status_id);
      let latestStatusHistoryItem = this.claim?.status_history.find((history: any) => history.status_id === this.claim?.status_id);
  
      switch(claimStatus) {
        case 'In Progress':
          let daysSinceSubmission;
          if (latestStatusHistoryItem) {
            daysSinceSubmission = moment().diff(moment(latestStatusHistoryItem.status_date), 'days');
          }
          claimStatusText = `${claimStatus} - ${daysSinceSubmission} Days`;
          break;
        case 'Completed': 
          if (!this.claim?.actual_amount) {
            claimStatusText = `Denied - ${moment.utc(latestStatusHistoryItem?.status_date).format('ll')}`;
          } else {
            claimStatusText = `${moment.utc(latestStatusHistoryItem?.status_date).format('ll')} - ${formatCurrencyString(this.claim?.actual_amount, this.userContextProvider.currencyCode)}`;
          }
          break;
        case 'Draft':
          claimStatusText = claimStatus;
          break;
        default: 
          claimStatusText = claimStatus;
      }
    }

    return claimStatusText;
  }

  getSendToText() {
    if (this.claim?.send_check_to == null) {
      return '';
    }

    switch(this.claim.send_check_to) {
      case 1:
        return 'Claim Payments via ACH';
      case 2:
        return 'PM';
      case 3:
        return 'Homeowner';
      default:
        return '';
    }
  }

  onBackClick(): void {
    this.router.navigate(['/claims']);
  }

  onModifyClaimClick(): void {
    this.router.navigate(['/claims/form'], { queryParams: { reservationId: this.claim?.reservation.id, claimId: this.claim?.id } });
  }

  onViewClaimPropertyClick() {
    this.router.navigate([`/properties/detail/${ this.claim?.property_id }`]);
  }

  onViewClaimReservationClick() {
    this.router.navigate([`/reservations/detail/${ this.claim?.reservation.id }`]);
  }

  onViewClaimOrganizationClick() {
    this.router.navigate([`/organizations/detail/${ this.claim?.organization_id }`]);
  }

  onViewReservationPolicyClick() {
    this.router.navigate([`/policies/detail/${ this.reservation.selected_policies[0].policy.id }`]);
  }

  onViewReservationGuestClick() {
    this.router.navigate([`/guests/detail/${ this.reservation.guests[0].guest_id }`]);
  }

  onInquireOpenClick(): void {
    const dialogRef = this.dialog.open(ClaimInquireModalComponent, {
      width: '55%'
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        this.claimService.sendInquiry(this.claimId, result.email, result.questionAbout, result.comment);
      }
    });
  }

  onAttachmentClick(attachmentId: number) {
    const attachment = this.attachments.find(attachment => attachment.file_id == attachmentId);
    if (!attachment) {
      this.logService.handleError('No attachment found', `No Claim attachment found for attachmentId: ${attachmentId}`, `No Claim attachment found for attachmentId: ${attachmentId}`);
      return;
    }
    const isImage = this.imageUtility.isImage(attachment?.file_name);

    this.dialog.open(ClaimDetailAttachmentDialogComponent, {
      width: '800px',
      maxHeight: isImage ? '1000px' : '600px',
      data: {
        isImage: isImage,
        document: attachment
      }
    });
  }

  getClaimStatusColor(statusId?: number): string {
    if (this.claim?.is_active === 0) {
      return '#FF0000';
    } else {
      if (statusId === 3) {
        if (!this.claim?.actual_amount || this.claim.actual_amount === 0) {
          statusId = 4;
        }
      }
      return getClaimStatusColor(statusId);
    }
  }

  onUpdateClaimStatusClick() {
    this.openUpdateClaimStatusDialog();
  }

  openUpdateClaimStatusDialog() {
    const statusDialogRef = this.dialog.open(UpdateClaimStatusDialogComponent, {
      width: '800px',
      maxHeight: '500px',
      disableClose: true,
      data: this.claim
    });
    statusDialogRef.afterClosed().subscribe((statusId: number) => {
      if (statusId > 0) {
        try {
          const statusUpdatePayload = {
            id: this.claim?.id.toString(),
            status_id: statusId.toString()
          }
          this.claimService.updateClaimStatus(this.claim?.id, statusUpdatePayload).then(_ => {
            this.loadClaim();
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Claim status', 'An error occurred while updating Claim status');
        }
      }
    });
  }

  onDeleteClaimClick() {
    this.openDeleteClaimDialog(true);
  }

  onUndeleteClaimClick() {
    this.openDeleteClaimDialog(false);
  }

  openDeleteClaimDialog(deleteClaim: boolean) {
    const deleteDialogRef = this.dialog.open(DeleteUndeleteClaimDialogComponent, {
      width: '700px',
      disableClose: true,
      data: {
        claim: this.claim,
        claimRow: this.claimRow,
        deleteClaim: deleteClaim
      }
    });
    deleteDialogRef.afterClosed().subscribe((statusId: number) => {
      try {
        if (statusId != null) {
          const deleteClaimPayload = {
            id: this.claim?.id.toString(),
            is_active: statusId,
            status_id: 1
          }

          this.claimService.updateClaimStatus(this.claim?.id, deleteClaimPayload).then(_ => {
            this.load();
          });
        }
      } catch (e) {
        this.logService.handleError(e, 'An error occurred while updating Claim status', 'An error occurred while updating Claim status');
      }
    });
  }

  onPaymentReceivedClick(): void {
    const dialogRef = this.dialog.open(ClaimPaymentReceivedDialogComponent, {
      width: '700px',
      data: this.claim
    });
    dialogRef.afterClosed().subscribe(async payload => {
      if (payload) {
        this.claimService.updateClaimDraft(this.claimId, payload).then(_ => {
          this.load();
        });
      }
    });
  }
}